import { useEffect, useRef, useState, useMemo, useCallback } from "react";
import { AgGridReact } from "ag-grid-react";
import { ModuleRegistry, AllCommunityModule } from "ag-grid-community";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-alpine.css";
import {
  globalConstants,
  modeConstants,
  recruiterConstants,
  typeConstants,
} from "../../constants";
import { getUserData } from "../../system";
import { useDispatch, useSelector } from "react-redux";
import { screenActions } from "../../redux/actions";
import { success } from "../../redux/actions/utilities";
import UploadExcelModal from "../Modals/UploadExcelModal";
import ServerPagination from "./ServerPagination";
import { convertDateToEpochSeconds, formatDateToCustomFormat, getPropertyValue, getStatusPill } from "../utility";

// Register AG Grid modules (must be after imports but before any other code)
ModuleRegistry.registerModules([AllCommunityModule]);

const STATUS_OPTIONS = [
  { value: "", label: "All Statuses" },
  { value: 0, label: "Pending" },
  { value: 1, label: "In Progress" },
  { value: 2, label: "Completed" },
  { value: 3, label: "Open" },
  { value: 4, label: "Hold" },
  { value: 5, label: "Closed" },
];

// Helper function to make cell content selectable (not copy on click)
const CopyableCell = ({ value, children }) => {
  const cellRef = useRef(null);

  return (
    <div
      ref={cellRef}
      className="copyable-cell"
      onDoubleClick={(e) => {
        // Select all text on double click for easier copying
        const range = document.createRange();
        range.selectNodeContents(e.currentTarget);
        const selection = window.getSelection();
        selection.removeAllRanges();
        selection.addRange(range);
      }}
    >
      <span
        className="cell-content"
      >
        {children || value || ""}
      </span>
    </div>
  );
};

// Loader Component
const TableLoader = () => {
  return (
    <div className="table-loader-container">
      <div className="spinner-wrapper">
        {/* Option 1: Pulsating Rings (Default) */}
        <div className="pulsating-rings-spinner">
          <div className="ring ring-1"></div>
          <div className="ring ring-2"></div>
          <div className="ring ring-3"></div>
          <div className="center-dot"></div>
        </div>
        
        {/* Optional: Add loading text with dots animation */}
        <div className="loading-text">
          <span className="loading-dots">
            <span className="dot dot-1">.</span>
            <span className="dot dot-2">.</span>
            <span className="dot dot-3">.</span>
          </span>
        </div>
        
        {/* Optional: Progress bar */}
        <div className="progress-container">
          <div className="progress-bar"></div>
        </div>
      </div>
    </div>
  );
};

export default function ActionsDataTable({
  actions,
  data,
  setGridState,
  setShowModal,
  onChangePage,
  onChangeRowsPerPage,
  paginationTotalRows,
  setMode,
  onSort,
  gridState,
  allowCreation = true,
  allowFilters = true,
  allowBulkActions = false,
  allowSelection = false,
  allowToolbar = true,
  sortServer = true,
  isDownloadTemplate = false,
  screenId,
  toggleStatus,
  openModal,
}) {
  const dispatch = useDispatch();
  const [showModal, setShowUploadModal] = useState(false);
  
  // SIMPLE loading state - only show loader when truly loading
  const [isLoading, setIsLoading] = useState(true);
  const [isInitialLoad, setIsInitialLoad] = useState(true);

  const screenColumns = useSelector((x) => x.screenReducer.screenColumns);
  const userPreferenceScreenColumns = useSelector(
    (x) => x.screenReducer.userPreferences
  );

  // AG Grid ref
  const gridRef = useRef(null);
  const [gridApi, setGridApi] = useState(null);
  const [columnApi, setColumnApi] = useState(null);

  // State for column filters and active popovers
  const [columnFilters, setColumnFilters] = useState({});
  const [pendingFilters, setPendingFilters] = useState({});
  const [activeFilterPopover, setActiveFilterPopover] = useState(null);

  // State for date filters
  const [dateRangeFilters, setDateRangeFilters] = useState({});

  // NEW: State for filter operation (AND/OR)
  const [filterOperation, setFilterOperation] = useState("AND"); // "AND" or "OR"

  // State for sorting
  const [sortState, setSortState] = useState({
    column: gridState?.sortColumn || null,
    direction: gridState?.sortDirection || null,
  });

  // State for column visibility and order
  const [columnVisibility, setColumnVisibility] = useState({});
  const [columnOrder, setColumnOrder] = useState([]);
  const [showColumnPanel, setShowColumnPanel] = useState(false);

  // Track if column order has changed but not yet saved
  const [unsavedColumnOrder, setUnsavedColumnOrder] = useState([]);
  const [hasUnsavedOrderChanges, setHasUnsavedOrderChanges] = useState(false);
  const [isUpdatingColumns, setIsUpdatingColumns] = useState(false);
  const [hasJustSaved, setHasJustSaved] = useState(false);
  const [initialSavedOrder, setInitialSavedOrder] = useState([]);

  // Refs for debouncing and preventing loops
  const isApplyingOrderRef = useRef(false);
  const debounceTimeoutRef = useRef(null);
  const lastColumnOrderRef = useRef([]);
  const hasMountedRef = useRef(false);
  const filterButtonRefs = useRef({});
  const popoverInputRef = useRef(null);
  const previousScreenIdRef = useRef(null);
  const savedOrderRef = useRef([]);
  const isManager = useSelector((s) => s.recruiterReducer.isManager);

  // Ref to store latest screen columns
  const screenColumnsRef = useRef(screenColumns);

  // Update ref whenever screenColumns changes
  useEffect(() => {
    screenColumnsRef.current = screenColumns;
  }, [screenColumns]);

  // SIMPLE LOADING LOGIC: Check if all required data is available
  useEffect(() => {
    // Check if all required data is loaded
    const hasScreenColumns = Array.isArray(screenColumns) && screenColumns.length > 0;
    const hasUserPreferences = Array.isArray(userPreferenceScreenColumns);
    const hasData = Array.isArray(data);
    
    // If all data is available, hide loader after a small delay
    if (hasScreenColumns && hasUserPreferences && hasData) {
      const timer = setTimeout(() => {
        setIsLoading(false);
        setIsInitialLoad(false);
      }, 500); // Small delay to ensure smooth transition
      
      return () => clearTimeout(timer);
    } else {
      setIsLoading(true);
    }
  }, [screenColumns, userPreferenceScreenColumns, data]);

  // Selection
  const [selectedRows, setSelectedRows] = useState([]);
  const [clearSelectionToggle, setClearSelectionToggle] = useState(false);
  const clearSelection = () => setClearSelectionToggle((v) => !v);

  // Bulk actions
  const bulkExport = () => {
    console.log("selectedRows", selectedRows);
  };

  const bulkDelete = () => {
    if (window.confirm(`Delete ${selectedRows.length} items?`)) {
      alert("Deleted (demo)");
      clearSelection();
    }
  };

  const onCreate = () => {
    setShowModal?.(true);
    setMode?.(modeConstants.ADD_MODE);
  };

  // Helper function to compare column orders
  const compareColumnOrders = useCallback((order1, order2) => {
    if (order1.length !== order2.length) return false;
    return order1.every((col, index) => col === order2[index]);
  }, []);

  // Reset column-related states when screenId changes
  useEffect(() => {
    if (previousScreenIdRef.current !== screenId) {
      // Reset all column-related states
      setColumnVisibility({});
      setColumnOrder([]);
      setUnsavedColumnOrder([]);
      setHasUnsavedOrderChanges(false);
      setHasJustSaved(false);
      setShowColumnPanel(false);
      setInitialSavedOrder([]);
      savedOrderRef.current = [];

      // Reset filter operation to default
      setFilterOperation("AND");

      // Update previous screenId
      previousScreenIdRef.current = screenId;
    }
  }, [screenId]);

  // Transform columns for the table
  const columns = useMemo(() => {
    // Ensure userPreferenceScreenColumns is an array
    const userPreferences = Array.isArray(userPreferenceScreenColumns)
      ? userPreferenceScreenColumns
      : [];

    // Ensure screenColumns is an array
    const availableScreenColumns = Array.isArray(screenColumns)
      ? screenColumns
      : [];

    // Create a map of user preferences by columnId
    const userPrefsByColumnId = {};
    userPreferences.forEach((pref) => {
      if (pref.columnId) {
        userPrefsByColumnId[pref.columnId] = {
          isVisible: pref.isVisible !== false,
          columnOrder: pref.columnOrder || 999,
          columnId: pref.columnId,
        };
      }
    });

    const columnsArray = [];

    // Always include Sr.No
    columnsArray.push({
      columnKey: "Sr.No",
      columnName: "Sr.No",
      selector: "sr_no",
      name: "Sr.No",
      minWidth: 70,
      maxWidth: 70,
    });

    // Get all regular screen columns (excluding Sr.No and Actions)
    const regularScreenColumns = availableScreenColumns.filter(
      (col) => col.columnKey !== "Sr.No" && col.columnKey !== "Actions"
    );

    // Start with all regular screen columns
    let visibleScreenColumns = [...regularScreenColumns];

    // Apply user preferences if they exist
    if (userPreferences.length > 0) {
      // Filter based on user preferences
      visibleScreenColumns = regularScreenColumns.filter((screenCol) => {
        const userPref = userPrefsByColumnId[screenCol.id];
        // If user has a preference, use it. Otherwise, show the column.
        return userPref ? userPref.isVisible : true;
      });

      // Sort by user's column order preference (starting from 1, not 0)
      visibleScreenColumns.sort((a, b) => {
        const orderA = userPrefsByColumnId[a.id]?.columnOrder || 999;
        const orderB = userPrefsByColumnId[b.id]?.columnOrder || 999;
        // If order is 0 (old format), treat it as 1
        const normalizedOrderA = orderA === 0 ? 1 : orderA;
        const normalizedOrderB = orderB === 0 ? 1 : orderB;
        return normalizedOrderA - normalizedOrderB;
      });
    }

    // Now filter based on current columnVisibility state (for real-time toggling)
    visibleScreenColumns = visibleScreenColumns.filter((screenCol) => {
      // Check if column is currently visible in columnVisibility state
      // If columnVisibility doesn't have an entry, default to true
      return columnVisibility[screenCol.columnKey] !== false;
    });

    // Add visible screen columns
    visibleScreenColumns.forEach((screenCol) => {
      // Handle columnType for cell rendering
      let cellRenderer = null;
      const columnType = screenCol.columnType || "text";

      if (columnType === typeConstants.IMAGE) {
        cellRenderer = (row) => {
          const imageUrl = getPropertyValue(
            row,
            screenCol.selector || screenCol.columnKey
          );
          if (imageUrl) {
            return (
              <img
                src={`${process.env.REACT_APP_STORAGE_URL}${imageUrl}`}
                alt="Logo"
                className="logo-circle"
              />
            );
          } else {
            return <div className="log-circle">No Logo</div>;
          }
        };
      } else if (columnType === typeConstants.TOGGLE) {
        cellRenderer = (row) => {
          const value = getPropertyValue(
            row,
            screenCol.selector || screenCol.columnKey
          );
          const isDisabled = screenCol.isDisabled; // Check if column is disabled
          return (
            <div
              className="form-check form-switch m-0"
              onClick={(e) => {
                e.stopPropagation();
                if (isDisabled) {
                  e.preventDefault(); // Prevent toggle if disabled
                  return;
                }
              }}
              title={
                isDisabled
                  ? "Toggle disabled"
                  : value
                  ? "Active"
                  : "Inactive"
              }
            >
              <input
                className="form-check-input"
                type="checkbox"
                role="switch"
                checked={!!value}
                onChange={() => {
                  if (!isDisabled) {
                    toggleStatus(row.id);
                  }
                }}
                disabled={isDisabled} // Disable the checkbox input
                aria-checked={!!value}
                style={{
                  cursor: isDisabled ? "not-allowed" : "pointer",
                  opacity: isDisabled ? 0.6 : 1,
                }}
              />
              {isDisabled && (
                <div
                  style={{
                    position: "absolute",
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    cursor: "not-allowed",
                    zIndex: 1,
                  }}
                  title="Toggle disabled"
                />
              )}
            </div>
          );
        };
      } else if (columnType === typeConstants.MODAL) {
        cellRenderer = (row) => {
          const value = getPropertyValue(
            row,
            screenCol.selector || screenCol.columnKey
          );
          return (
            <a onClick={() => openModal(row)} className="cursor-pointer">
              {value}
            </a>
          );
        };
      } else if (columnType === typeConstants.LINK) {
        cellRenderer = (row) => {
          const value = getPropertyValue(
            row,
            screenCol.selector || screenCol.columnKey
          );

          if (!value) return "";

          const isFullUrl =
            value.startsWith("http://") ||
            value.startsWith("https://") ||
            value.includes(".com");

          const href = isFullUrl
            ? value
            : `${process.env.REACT_APP_STORAGE_URL}${value}`;

          return (
            <a href={href} target="_blank" rel="noopener noreferrer">
              <i className="bi bi-eye text-success"></i>
            </a>
          );
        };
      } else if (columnType === typeConstants.DATE) {
        cellRenderer = (row) => {
          const value = getPropertyValue(
            row,
            screenCol.selector || screenCol.columnKey
          );
          const displayValue = formatDateToCustomFormat(value);
          return <CopyableCell value={displayValue}>{displayValue}</CopyableCell>;
        };
      } else if (columnType === typeConstants.STATUS) {
        // New status column type - uses getStatusPill utility function
        cellRenderer = (row) => {
          const statusValue = getPropertyValue(
            row,
            screenCol.selector || screenCol.columnKey
          );
          return getStatusPill(statusValue);
        };
      } else {
        // Default text cell renderer - wrapped in CopyableCell
        cellRenderer = (row) => {
          const value = getPropertyValue(
            row,
            screenCol.selector || screenCol.columnKey
          );
          return <CopyableCell value={value}>{value}</CopyableCell>;
        };
      }

      columnsArray.push({
        columnKey: screenCol.columnKey,
        columnName: screenCol.columnName || screenCol.columnKey,
        selector: screenCol.selector || screenCol.columnKey,
        name: screenCol.columnName || screenCol.columnKey,
        columnType: screenCol.columnType || "text",
        ...(screenCol.minWidth && { minWidth: screenCol.minWidth }),
        ...(screenCol.maxWidth && { maxWidth: screenCol.maxWidth }),
        sortable: screenCol.sortable === true, // Use screenColumn.sortable value
        cell: cellRenderer,
        visible: true,
        order: userPrefsByColumnId[screenCol.id]?.columnOrder || 999,
      });
    });

    // Use passed actions for Actions column
    if (actions) {
      columnsArray.push({
        columnKey: "Actions",
        columnName: actions.name || "Actions",
        selector: "actions",
        name: actions.name || "Actions",
        minWidth: actions.minWidth || 140,
        maxWidth: actions.maxWidth || 140,
        cell: actions.cell,
        sortable: false, // Actions column is not sortable
        ignoreRowClick: actions.ignoreRowClick || true,
        allowOverflow: actions.allowOverflow || true,
        button: actions.button || true,
      });
    } else {
      columnsArray.push({
        columnKey: "Actions",
        columnName: "Actions",
        selector: "actions",
        name: "Actions",
        minWidth: 140,
        maxWidth: 140,
        cell: (row) => "No Actions",
        sortable: false,
      });
    }

    return columnsArray;
  }, [
    userPreferenceScreenColumns,
    screenColumns,
    actions,
    toggleStatus,
    columnVisibility,
    openModal,
  ]);

  // Initialize column visibility and order from columns
  useEffect(() => {
    if (screenColumns?.length > 0 && previousScreenIdRef.current === screenId) {
      const visibility = {};
      const order = [];
      const userPreferences = Array.isArray(userPreferenceScreenColumns)
        ? userPreferenceScreenColumns
        : [];

      // Create a map of user preferences by columnId
      const userPrefsByColumnId = {};
      userPreferences.forEach((pref) => {
        if (pref.columnId) {
          userPrefsByColumnId[pref.columnId] = {
            isVisible: pref.isVisible !== false,
            columnOrder: pref.columnOrder || 999,
          };
        }
      });

      // For each screen column, set initial visibility
      screenColumns.forEach((col) => {
        const colKey = col.columnKey;
        if (colKey && colKey !== "Sr.No" && colKey !== "Actions") {
          const userPref = userPrefsByColumnId[col.id];

          if (userPreferences.length > 0) {
            // If user preferences exist, use them
            visibility[colKey] = userPref ? userPref.isVisible : true;
          } else {
            // If no user preferences, all columns are visible by default
            visibility[colKey] = true;
          }

          if (visibility[colKey]) {
            order.push(colKey);
          }
        }
      });

      // Preserve any existing visibility state for columns that might have been toggled
      setColumnVisibility((prev) => ({
        ...prev, // Keep existing state
        ...visibility, // Add/update with initial values
      }));

      // Update order
      setColumnOrder(order);
      setUnsavedColumnOrder(order);
      setInitialSavedOrder(order);
      savedOrderRef.current = order;
      lastColumnOrderRef.current = order;
    }
  }, [screenColumns, userPreferenceScreenColumns, screenId]);

  // Handle column visibility toggle
  const toggleColumnVisibility = useCallback(
    (columnKey, isVisible) => {
      // Update local state first for immediate feedback
      setColumnVisibility((prev) => ({
        ...prev,
        [columnKey]: isVisible,
      }));

      // Apply to grid if available
      if (columnApi) {
        try {
          const column = columnApi.getColumn(columnKey);
          if (column) {
            columnApi.setColumnVisible(columnKey, isVisible);
          }
        } catch (error) {
          console.warn("Failed to toggle column visibility:", error);
        }
      }

      // Find the column ID from screenColumns and save to user preferences
      const screenCol = screenColumns?.find(
        (col) => col.columnKey === columnKey
      );
      if (screenCol) {
        // Save to user preferences using columnId
        dispatch(
          screenActions.updateColumnPreferencesByColumnId({
            screenId: screenId,
            columnId: screenCol.id,
            isVisible: isVisible,
          })
        );
      }
    },
    [columnApi, screenId, dispatch, screenColumns, compareColumnOrders]
  );

  // Handle column drag - update both columns that were swapped
  const handleColumnMoved = useCallback(
    (params) => {
      // Only process if this is an actual column drag (1 column in params)
      if (!params.columns || params.columns.length !== 1) {
        console.log("Skipping - not a single column drag");
        return;
      }

      // Only process user drag events
      if (params.source !== "uiColumnMoved") {
        console.log("Skipping - not a user drag event");
        return;
      }

      // Don't process if we're currently updating columns
      if (isUpdatingColumns || isApplyingOrderRef.current) {
        console.log("Skipping - updating columns in progress");
        return;
      }

      // Clear any existing timeout
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }

      debounceTimeoutRef.current = setTimeout(() => {
        try {
          const api = params?.api || gridRef.current?.api;
          if (!api) return;

          // Get current column order from AG Grid
          const columnState = api.getColumnState();
          if (!columnState || columnState.length === 0) return;

          // Extract column IDs, filtering out pinned columns
          const currentOrder = columnState
            .filter((col) => !col.pinned)
            .map((col) => col.colId)
            .filter((id) => id !== "sr_no" && id !== "actions");

          // Check if order actually changed by comparing with saved order
          const arraysEqual = (a, b) =>
            a.length === b.length && a.every((val, index) => val === b[index]);

          if (arraysEqual(currentOrder, savedOrderRef.current)) {
            setHasUnsavedOrderChanges(false);
            return;
          }

          // Update ref with new order
          lastColumnOrderRef.current = currentOrder;

          // Update state with new order
          setColumnOrder(currentOrder);
          setUnsavedColumnOrder(currentOrder);

          // Set unsaved changes flag
          setHasUnsavedOrderChanges(true);
          setHasJustSaved(false);

          // IMPORTANT: Apply the dragged position back to the grid
          // This ensures the column stays in the dragged position
          if (columnApi && currentOrder.length > 0) {
            try {
              const allColumns = columnApi.getAllColumns();
              if (allColumns) {
                const currentGridOrder = allColumns.map((col) =>
                  col.getColId()
                );
                const pinnedLeftColumns = currentGridOrder.filter(
                  (id) => id === "sr_no" || id.includes("Actions")
                );
                const reorderableColumns = currentOrder.filter(
                  (id) =>
                    !pinnedLeftColumns.includes(id) && columnApi.getColumn(id)
                );

                const finalOrder = [...pinnedLeftColumns, ...reorderableColumns];
                columnApi.setColumnOrder(finalOrder);
              }
            } catch (error) {
              console.warn("Failed to apply dragged order to grid:", error);
            }
          }

          // Don't save to server here - user will manually save
        } catch (error) {
          console.warn("Error handling column move:", error);
        }
      }, 100);
    },
    [isUpdatingColumns, columnApi]
  );

  // Save column order to database (called manually via button)
  const saveColumnOrder = useCallback(async () => {
    if (!screenId || !unsavedColumnOrder.length) return;

    try {
      setIsUpdatingColumns(true);
      isApplyingOrderRef.current = true;

      // Get fresh screen columns from ref
      const currentScreenColumns = screenColumnsRef.current;

      if (
        screenId &&
        currentScreenColumns &&
        Array.isArray(currentScreenColumns)
      ) {
        // Filter to get only columns for the current screen
        const screenSpecificColumns = currentScreenColumns.filter(
          (col) => col.screenId === screenId
        );

        const columnUpdates = unsavedColumnOrder
          .map((columnKey, index) => {
            // Find the screen column by columnKey
            const screenCol = screenSpecificColumns.find(
              (col) => col.columnKey === columnKey
            );

            if (!screenCol) {
              console.warn(`Column ${columnKey} not found in screen columns`);
              return null;
            }

            return {
              screenId: screenId,
              columnId: screenCol.id,
              columnOrder: index + 1, // Changed from index to index + 1 (1-based)
              isVisible: columnVisibility[columnKey] !== false,
            };
          })
          .filter(Boolean);

        // Save to server
        if (columnUpdates.length > 0) {
          // Execute updates sequentially
          const updatePromises = columnUpdates.map((update) =>
            dispatch(screenActions.updateColumnOrderById(update))
          );

          // Wait for all updates to complete
          await Promise.all(updatePromises);

          // Then refresh the data
          dispatch(screenActions.getColumnsByScreenId(screenId));
          dispatch(screenActions.getUserPreferencesByScreenId(screenId));
        }

        // Also update hidden columns
        const hiddenColumns = Object.keys(columnVisibility)
          .filter((key) => columnVisibility[key] === false)
          .map((key) => {
            const screenCol = screenSpecificColumns.find(
              (col) => col.columnKey === key
            );
            return screenCol
              ? {
                  screenId,
                  columnId: screenCol.id,
                  columnOrder: 1000 + unsavedColumnOrder.length, // Start from higher number
                  isVisible: false,
                }
              : null;
          })
          .filter(Boolean);

        for (const update of hiddenColumns) {
          await dispatch(screenActions.updateUserColumnPreference(update));
        }

        // CRITICAL FIX: Update saved order reference FIRST
        savedOrderRef.current = [...unsavedColumnOrder];

        // Then update the UI state
        setHasUnsavedOrderChanges(false); // This will hide the save button
        setHasJustSaved(true);

        // Update the column order state to match what was saved
        setColumnOrder([...unsavedColumnOrder]);
        setInitialSavedOrder([...unsavedColumnOrder]);

        // Hide success message after 2 seconds
        setTimeout(() => {
          setHasJustSaved(false);
        }, 2000);
      } else {
        console.error("Missing screenId or screenColumns:", {
          screenId: screenId,
          hasScreenColumns: !!currentScreenColumns,
          isArray: Array.isArray(currentScreenColumns),
        });
      }
    } catch (error) {
      console.error("Failed to save column order:", error);
      // If save fails, keep the unsaved changes state
      setHasUnsavedOrderChanges(false);
      setHasJustSaved(false);
    } finally {
      setIsUpdatingColumns(false);
      isApplyingOrderRef.current = false;
    }
  }, [
    screenId,
    unsavedColumnOrder,
    screenColumns,
    columnVisibility,
    dispatch,
  ]);

  // Reset column order to default
  const resetColumnOrder = useCallback(async () => {
    if (!screenId || !screenColumns) return;

    try {
      setIsUpdatingColumns(true);

      // Get fresh screen columns from ref
      const currentScreenColumns = screenColumnsRef.current;

      if (
        screenId &&
        currentScreenColumns &&
        Array.isArray(currentScreenColumns)
      ) {
        // Filter to get only columns for the current screen
        const screenSpecificColumns = currentScreenColumns.filter(
          (col) => col.screenId === screenId
        );

        // Create default order (all visible columns sorted by columnKey)
        const defaultOrder = screenSpecificColumns
          .filter((col) => columnVisibility[col.columnKey] !== false)
          .map((col) => col.columnKey)
          .sort();

        // Update local state
        setColumnOrder(defaultOrder);
        setUnsavedColumnOrder(defaultOrder);
        lastColumnOrderRef.current = defaultOrder;

        // Compare with saved order
        const hasChanges = !compareColumnOrders(
          defaultOrder,
          savedOrderRef.current
        );
        setHasUnsavedOrderChanges(hasChanges);

        if (hasChanges) {
          setHasJustSaved(false);
        }

        // Apply to grid if available
        if (columnApi && defaultOrder.length > 0) {
          try {
            const allColumns = columnApi.getAllColumns();
            if (allColumns) {
              const currentOrder = allColumns.map((col) => col.getColId());
              const pinnedLeftColumns = currentOrder.filter(
                (id) => id === "sr_no" || id.includes("Actions")
              );
              const reorderableColumns = defaultOrder.filter(
                (id) =>
                  !pinnedLeftColumns.includes(id) && columnApi.getColumn(id)
              );

              const finalOrder = [...pinnedLeftColumns, ...reorderableColumns];
              columnApi.setColumnOrder(finalOrder);
            }
          } catch (error) {
            console.warn("Failed to apply column order:", error);
          }
        }
      }
    } catch (error) {
      console.error("Failed to reset column order:", error);
    } finally {
      setIsUpdatingColumns(false);
    }
  }, [
    screenId,
    screenColumns,
    columnVisibility,
    columnApi,
    compareColumnOrders,
  ]);

  // Apply column visibility to grid when columnApi is available
  useEffect(() => {
    if (columnApi && Object.keys(columnVisibility).length > 0) {
      try {
        Object.entries(columnVisibility).forEach(([colId, isVisible]) => {
          const column = columnApi.getColumn(colId);
          if (column) {
            columnApi.setColumnVisible(colId, isVisible);
          }
        });
      } catch (error) {
        console.warn("Failed to apply column visibility:", error);
      }
    }
  }, [columnApi, columnVisibility]);

  // Apply column order to grid when columnApi is available
  useEffect(() => {
    if (columnApi && columnOrder.length > 0) {
      try {
        const allColumns = columnApi.getAllColumns();
        if (allColumns) {
          const currentGridOrder = allColumns.map((col) => col.getColId());

          // Only apply order if it's different from current grid order
          const pinnedLeftColumns = currentGridOrder.filter(
            (id) => id === "sr_no" || id.includes("Actions")
          );
          const currentReorderableColumns = currentGridOrder.filter(
            (id) => !pinnedLeftColumns.includes(id)
          );

          const targetReorderableColumns = columnOrder.filter(
            (id) =>
              !pinnedLeftColumns.includes(id) && columnApi.getColumn(id)
          );

          // Check if reorderable columns are different
          const arraysEqual = (a, b) =>
            a.length === b.length && a.every((val, index) => val === b[index]);

          if (
            !arraysEqual(currentReorderableColumns, targetReorderableColumns)
          ) {
            const finalOrder = [...pinnedLeftColumns, ...targetReorderableColumns];
            columnApi.setColumnOrder(finalOrder);
          }
        }
      } catch (error) {
        console.warn("Failed to apply column order:", error);
      }
    }
  }, [columnApi, columnOrder]);

  // Close popover when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        activeFilterPopover &&
        !event.target.closest(".filter-popover") &&
        !event.target.closest(".filter-icon-btn")
      ) {
        setActiveFilterPopover(null);
      }

      // Close column panel when clicking outside
      if (
        showColumnPanel &&
        !event.target.closest(".column-management-panel") &&
        !event.target.closest(".column-management-btn")
      ) {
        setShowColumnPanel(false);
      }
    };

    document.addEventListener("click", handleClickOutside);
    return () => document.removeEventListener("click", handleClickOutside);
  }, [activeFilterPopover, showColumnPanel]);

  // Focus input when popover opens
  useEffect(() => {
    if (activeFilterPopover && popoverInputRef.current) {
      setTimeout(() => {
        popoverInputRef.current?.focus();
      }, 100);
    }
  }, [activeFilterPopover]);

  // Initialize sort state from gridState
  useEffect(() => {
    if (gridState?.sortColumn && gridState?.sortDirection) {
      setSortState({
        column: gridState.sortColumn,
        direction: gridState.sortDirection,
      });
    }
  }, [gridState?.sortColumn, gridState?.sortDirection]);

  // NEW: Generate filterable columns from screen columns - only for sortable columns
  const filterableColumns = useMemo(() => {
    if (!screenColumns || !Array.isArray(screenColumns)) return [];

    return screenColumns
      .filter(
        (col) =>
          col.columnKey &&
          col.columnKey !== "Sr.No" &&
          col.columnKey !== "Actions"
      )
      .filter((col) => col.sortable === true) // Only include sortable columns
      .map((col) => {
        // Determine column type based on columnType
        let type = "string";
        if (col.columnType === "date") type = "date";
        if (col.columnType === "toggle") type = "boolean";
        if (col.columnType === "status") type = "status"; // Changed to "status" for status columns
        if (col.columnType === "number") type = "number";

        return {
          key: col.filterSelector || col.selector || col.columnKey, // CHANGED: Use filterSelector first
          label: col.columnName || col.columnKey,
          type: type,
          selector: col.selector || col.columnKey,
          columnKey: col.columnKey, // Keep original columnKey for reference
          filterSelector: col.filterSelector, // Store filterSelector separately
        };
      });
  }, [screenColumns]);

  // Filter helpers - FIXED: Use selector as key
  const getColMeta = (columnKey) => {
    // First try to get from filterableColumns
    const fromFilterable = filterableColumns?.find(
      (c) => c.columnKey === columnKey
    );
    if (fromFilterable) return fromFilterable;

    // If not found, look in screenColumns
    const screenCol = screenColumns?.find((col) => col.columnKey === columnKey);
    if (!screenCol) return null;

    // Determine column type based on columnType
    let type = "string";
    if (screenCol.columnType === "date") type = "date";
    if (screenCol.columnType === "toggle") type = "boolean";
    if (screenCol.columnType === "status") type = "status"; // Changed to "status"
    if (screenCol.columnType === "number") type = "number";

    return {
      key: screenCol.filterSelector || screenCol.selector || screenCol.columnKey,
      label: screenCol.columnName || screenCol.columnKey,
      type: type,
      selector: screenCol.selector || screenCol.columnKey,
      columnKey: screenCol.columnKey,
      filterSelector: screenCol.filterSelector,
    };
  };

  const defaultOpFor = (type) => (type === "number" ? "EqualTo" : "Contains");

  const stringOps = [
    { value: "Contains", label: "Contains" },
    { value: "StartsWith", label: "StartsWith" },
    { value: "EndsWith", label: "EndsWith" },
    { value: "EqualTo", label: "EqualTo" },
  ];
  const numberOps = [{ value: "EqualTo", label: "EqualTo" }];
  const booleanOps = [{ value: "EqualTo", label: "EqualTo" }];
  const statusOps = [{ value: "EqualTo", label: "EqualTo" }]; // Status columns only need EqualTo

  const opsForType = (type) => {
    if (type === "number") return numberOps;
    if (type === "boolean") return booleanOps;
    if (type === "status") return statusOps; // Added status type
    return stringOps;
  };

  const toBase64 = (s) => btoa(unescape(encodeURIComponent(s)));
  const escapeForDQString = (s) =>
    String(s).replace(/\\/g, "\\\\").replace(/"/g, '\\"');

  // Build filter expression with date range support - uses filterSelector for filtering
  const buildColumnFilterExpr = (
    columnKey,
    filterValue,
    operator = "Contains"
  ) => {
    if (!columnKey) return "";

    const meta = getColMeta(columnKey);
    if (!meta) return "";

    const type = meta.type || "string";
    const op = operator;
    // CHANGED: Use filterSelector if available, otherwise use selector
    const filterKey = meta.filterSelector || meta.selector || meta.columnKey;

    // For date type with range
    if (type === "date") {
      const dateRange = dateRangeFilters[columnKey];
      if (dateRange && (dateRange.startDate || dateRange.endDate)) {
        const startEpoch = dateRange.startDate
          ? convertDateToEpochSeconds(dateRange.startDate)
          : null;
        const endEpoch = dateRange.endDate
          ? convertDateToEpochSeconds(dateRange.endDate)
          : null;

        if (startEpoch && endEpoch) {
          return `(${filterKey}>=(${startEpoch}))&&(${filterKey}<=(${endEpoch}))`;
        } else if (startEpoch) {
          return `${filterKey}>=(${startEpoch})`;
        } else if (endEpoch) {
          return `${filterKey}<=(${endEpoch})`;
        }
      }
    }

    // For regular filters (text, number, boolean, status)
    if (filterValue === null || filterValue === undefined || filterValue === "") return "";

    const value = String(filterValue).trim();

    if (type === "number") {
      const n = Number(value);
      if (!Number.isFinite(n)) return "";
      if (op === "EqualTo") return `${filterKey}==(${n})`;
      return "";
    }

    if (type === "boolean") {
      const val = value === true || value === "true" ? "true" : "false";
      return `${filterKey}==(${val})`;
    }

    if (type === "status") {
      const n = Number(value);
      if (!Number.isFinite(n)) return "";
      return `${filterKey}==(${n})`; // Always use EqualTo for status
    }

    const v = escapeForDQString(value);
    switch (op) {
      case "Contains":
        return `${filterKey}.Contains("${v}")`;
      case "StartsWith":
        return `${filterKey}.StartsWith("${v}")`;
      case "EndsWith":
        return `${filterKey}.EndsWith("${v}")`;
      case "EqualTo":
        return `${filterKey}==("${v}")`;
      default:
        return "";
    }
  };

  // Build combined filter expression with AND/OR operator
  const buildCombinedFilterExpression = (
    filters = columnFilters,
    operation = filterOperation
  ) => {
    const filterParts = [];

    // Process regular filters
    Object.entries(filters).forEach(([columnKey, filterData]) => {
      const expr = buildColumnFilterExpr(
        columnKey,
        filterData?.value,
        filterData?.operator
      );
      if (expr) {
        filterParts.push(expr);
      }
    });

    // Process date range filters separately
    Object.entries(dateRangeFilters).forEach(([columnKey, dateRange]) => {
      const expr = buildColumnFilterExpr(columnKey, "");
      if (expr) {
        filterParts.push(expr);
      }
    });

    if (filterParts.length === 0) return globalConstants.FILTER_EXPRESSION;

    // Use the selected operator (AND or OR)
    const operator = operation === "OR" ? "||" : "&&";
    return toBase64(filterParts.join(operator));
  };

  // Handle filter input change - FIXED for status values 0 and 1
  const handleFilterInputChange = (
    columnKey,
    value,
    operator = "Contains"
  ) => {
    setPendingFilters((prev) => {
      const newPending = { ...prev };

      // FIX: Check for empty string, null, or undefined
      if (value === "" || value === null || value === undefined) {
        delete newPending[columnKey];
      } else {
        newPending[columnKey] = { value, operator };
      }

      return newPending;
    });
  };

  // Handle date range change
  const handleDateRangeChange = (columnKey, startDate, endDate) => {
    setDateRangeFilters((prev) => {
      const newDateRanges = { ...prev };

      if (!startDate && !endDate) {
        delete newDateRanges[columnKey];
      } else {
        newDateRanges[columnKey] = { startDate, endDate };
      }

      return newDateRanges;
    });
  };

  // Apply filters function
  const applyFilters = () => {
    const mergedFilters = { ...columnFilters };
    Object.keys(pendingFilters).forEach((key) => {
      if (pendingFilters[key]?.value !== undefined && pendingFilters[key]?.value !== "") {
        mergedFilters[key] = pendingFilters[key];
      } else {
        delete mergedFilters[key];
      }
    });

    setColumnFilters(mergedFilters);
    setPendingFilters({});
    setActiveFilterPopover(null);

    // Build expression with both regular and date range filters
    const newExpression = buildCombinedFilterExpression(
      mergedFilters,
      filterOperation
    );

    setGridState({
      ...gridState,
      filterExpression: newExpression,
      pageNumber: 1,
    });
  };

  // Cancel pending filter changes
  const cancelFilterChanges = () => {
    setPendingFilters({});
    setActiveFilterPopover(null);
  };

  // Clear specific column filter
  const clearColumnFilter = (columnKey) => {
    const newFilters = { ...columnFilters };
    delete newFilters[columnKey];

    setColumnFilters(newFilters);
    setPendingFilters((prev) => {
      const newPending = { ...prev };
      delete newPending[columnKey];
      return newPending;
    });

    // Also clear date range filter if exists
    setDateRangeFilters((prev) => {
      const newDateRanges = { ...prev };
      delete newDateRanges[columnKey];
      return newDateRanges;
    });

    const newExpression = buildCombinedFilterExpression(newFilters);
    setGridState({
      ...gridState,
      filterExpression: newExpression,
      pageNumber: 1,
    });
    setActiveFilterPopover(null);
  };

  // Clear all filters
  const clearAllFilters = () => {
    setColumnFilters({});
    setPendingFilters({});
    setDateRangeFilters({});
    setActiveFilterPopover(null);
    setGridState({
      ...gridState,
      filterExpression: globalConstants.FILTER_EXPRESSION,
      pageNumber: 1,
    });
  };

  // Handle filter operation change
  const handleFilterOperationChange = (operation) => {
    setFilterOperation(operation);

    // Only re-apply filters if we have active filters
    if (
      Object.keys(columnFilters).length > 0 ||
      Object.keys(dateRangeFilters).length > 0
    ) {
      // Use the new operation to build the expression
      const newExpression = buildCombinedFilterExpression(
        columnFilters,
        operation
      );

      // Update grid state with the new expression
      setGridState({
        ...gridState,
        filterExpression: newExpression,
        pageNumber: 1, // Reset to first page when changing operation
      });
    }
  };

  // Toggle filter popover
  const toggleFilterPopover = (columnKey, event) => {
    event.stopPropagation();
    event.preventDefault();
    setActiveFilterPopover((prev) => (prev === columnKey ? null : columnKey));
  };

  // Get current filter value for display - FIXED for status values
  const getFilterDisplayValue = (columnKey) => {
    if (pendingFilters[columnKey] !== undefined) {
      return pendingFilters[columnKey];
    }
    return columnFilters[columnKey];
  };

  // Get date range filter value
  const getDateRangeFilterValue = (columnKey) => {
    return dateRangeFilters[columnKey] || {};
  };

  // Get filter status for a column
  const getColumnFilterStatus = (columnKey) => {
    const meta = getColMeta(columnKey);
    const isDateColumn = meta?.type === "date";

    const hasActiveFilter = columnFilters[columnKey] !== undefined && 
                          columnFilters[columnKey]?.value !== undefined && 
                          columnFilters[columnKey]?.value !== "";
    const hasPendingFilter = pendingFilters[columnKey] !== undefined && 
                           pendingFilters[columnKey]?.value !== undefined && 
                           pendingFilters[columnKey]?.value !== "";
    const hasDateRangeFilter = isDateColumn && !!dateRangeFilters[columnKey];
    const hasAnyFilter = hasActiveFilter || hasPendingFilter || hasDateRangeFilter;

    return {
      hasActiveFilter,
      hasPendingFilter,
      hasDateRangeFilter,
      hasAnyFilter,
      buttonClass: hasPendingFilter
        ? "btn-warning"
        : hasAnyFilter
        ? "btn-primary"
        : "btn-outline-secondary",
      // Changed from funnel icon to menu icon
      buttonIcon: hasPendingFilter
        ? "bi-clock"
        : hasAnyFilter
        ? "bi-list"
        : "bi-list",
      buttonTitle: hasPendingFilter
        ? "Pending changes - Click to apply"
        : hasDateRangeFilter
        ? "Filter & sort options"
        : "Filter & sort options",
    };
  };

  // Handle sort selection - NEW: Now called from filter popover
  const handleSortChange = (columnKey, direction) => {
    const newSortState = {
      column: direction ? columnKey : null,
      direction: direction,
    };

    setSortState(newSortState);

    if (onSort) {
      // Find the screen column to get the filterSelector
      const screenCol = screenColumns?.find(
        (col) => col.columnKey === columnKey
      );
      // Use filterSelector first, then selector, then columnKey
      const sortField = screenCol?.filterSelector || screenCol?.selector || columnKey;

      // Pass the filterSelector for sorting
      onSort(sortField, newSortState.direction);
    }
  };

  // Get sort icon for a column
  const getSortIcon = (columnKey) => {
    if (sortState.column !== columnKey) {
      return "bi-arrow-down-up";
    }

    switch (sortState.direction) {
      case "asc":
        return "bi-sort-up";
      case "desc":
        return "bi-sort-down";
      default:
        return "bi-arrow-down-up";
    }
  };

  // Get sort tooltip for a column
  const getSortTooltip = (columnKey) => {
    if (sortState.column !== columnKey) {
      return "Click to sort ascending";
    }

    switch (sortState.direction) {
      case "asc":
        return "Sorted ascending - Click for descending";
      case "desc":
        return "Sorted descending - Click to clear";
      default:
        return "Click to sort ascending";
    }
  };

  // Get sort button class
  const getSortButtonClass = (columnKey) => {
    if (sortState.column !== columnKey) {
      return "btn-outline-secondary";
    }

    switch (sortState.direction) {
      case "asc":
        return "btn-primary";
      case "desc":
        return "btn-primary";
      default:
        return "btn-outline-secondary";
    }
  };

  // Clear all sorting
  const clearAllSorting = () => {
    setSortState({ column: null, direction: null });

    if (onSort) {
      onSort(null, null);
    }
  };

  // Transform React Data Table columns to AG Grid columns
  const getAGGridColumns = useMemo(() => {
    if (!columns || columns.length === 0) return [];

    return columns.map((col) => {
      // Find filterable column by matching columnKey - only for sortable columns
      const filterableColumn = col.sortable
        ? filterableColumns?.find((fc) => fc.columnKey === col.columnKey)
        : null;

      // Use columnKey as-is for field and colId
      const colId = col.columnKey;
      const field = col.selector || col.columnKey; // Use selector as field

      // Create base column definition
      const columnDef = {
        headerName: col.columnName || col.columnKey,
        field: field,
        colId: colId,
        sortable: false,
        filter: false,
        wrapText: true,
        autoHeight: true,
        resizable: true,
        // width: col.width || undefined,
        // minWidth: col.minWidth ? parseInt(col.minWidth) : 100,
        // maxWidth: col.maxWidth || undefined,
        suppressSizeToFit: false,
        cellStyle: {
          whiteSpace: "normal",
          overflow: "visible",
          textOverflow: "clip",
          wordBreak: "break-word",
          lineHeight: "1.5",
        },
        // IMPORTANT: Prevent all columns from being moved into pinned areas
        lockPosition:
          col.columnKey === "Sr.No" || col.columnKey === "Actions"
            ? true
            : "right",
        suppressMovable:
          col.columnKey === "Sr.No" || col.columnKey === "Actions",
        headerClass: "black-header", // Custom class for all headers
      };

      // Handle special columns
      if (col.columnKey === "Sr.No") {
        columnDef.pinned = "left";
        columnDef.width = 70;
        columnDef.lockPosition = true; // Absolutely locked in position
        columnDef.suppressMovable = true; // Cannot be moved
        columnDef.cellStyle = {
          ...columnDef.cellStyle,
          whiteSpace: "nowrap",
          textAlign: "left", // Left aligned
        };
        columnDef.valueGetter = (params) => {
          // Calculate Sr.No based on current page
          const currentPage = gridState?.pageNumber || 1;
          const pageSize = gridState?.length || 10;
          return (currentPage - 1) * pageSize + params.node.rowIndex + 1;
        };
        // Make Sr.No copyable
        columnDef.cellRenderer = (params) => {
          const value = params.value;
          return <CopyableCell value={value}>{value}</CopyableCell>;
        };
      } else if (col.columnKey === "Actions") {
        columnDef.pinned = "right";
        columnDef.width = col.minWidth || 140;
        columnDef.lockPosition = true; // Absolutely locked in position
        columnDef.suppressMovable = true; // Cannot be moved
        columnDef.cellStyle = {
          ...columnDef.cellStyle,
          whiteSpace: "nowrap",
          display: "flex",
          alignItems: "center",
          justifyContent: "center", // Keep Actions centered
        };

        // Use the passed actions cell renderer
        if (col.cell) {
          columnDef.cellRenderer = (params) => {
            try {
              return col.cell(params.data, params.node.rowIndex);
            } catch (error) {
              console.error("Error rendering actions:", error);
              return "Error";
            }
          };
        }
      } else {
        // For regular columns - left aligned
        columnDef.cellStyle = {
          ...columnDef.cellStyle,
          textAlign: "left", // Left aligned
          display: "flex",
          alignItems: "center",
        };

        // Regular columns cannot be moved left of Sr.No or right of Actions
        columnDef.lockPosition = "right"; // Can only be moved within the regular columns area
        columnDef.suppressMovable = false; // Can be moved within regular columns

        if (col.cell) {
          columnDef.cellRenderer = (params) => {
            try {
              const result = col.cell(params.data, params.node.rowIndex);
              return result;
            } catch (error) {
              console.error(`Error rendering column ${col.columnKey}:`, error);
              // Fallback to using getPropertyValue for nested properties
              const value = getPropertyValue(
                params.data,
                col.selector || col.columnKey
              );
              return (
                <CopyableCell value={value}>
                  {value !== undefined ? value : ""}
                </CopyableCell>
              );
            }
          };
        } else if (col.columnType === "status") {
          // Special handling for status column type
          columnDef.cellRenderer = (params) => {
            try {
              const statusValue = getPropertyValue(
                params.data,
                col.selector || col.columnKey
              );
              return getStatusPill(statusValue);
            } catch (error) {
              console.error(
                `Error rendering status column ${col.columnKey}:`,
                error
              );
              const value = getPropertyValue(
                params.data,
                col.selector || col.columnKey
              );
              return value !== undefined ? String(value) : "";
            }
          };
        } else {
          // Default cell renderer for columns without custom cell function
          // Uses getPropertyValue for nested properties and makes it copyable
          columnDef.valueGetter = (params) => {
            return getPropertyValue(params.data, col.selector || col.columnKey);
          };
          columnDef.cellRenderer = (params) => {
            const value = params.value;
            return (
              <CopyableCell value={value}>
                {value !== undefined ? value : ""}
              </CopyableCell>
            );
          };
        }

        // Add filter button only (sorting moved to filter popover)
        if (
          col.sortable &&
          col.columnKey !== "Sr.No" &&
          col.columnKey !== "Actions" &&
          allowFilters
        ) {
          const status = getColumnFilterStatus(col.columnKey);

          columnDef.headerComponent = () => (
            <div className="ag-cell-label-container" role="presentation">
              <div
                className="ag-header-cell-label"
              >
                <span
                  className="ag-header-cell-text"
                >
                  {col.columnName}
                </span>
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "4px",
                    marginLeft: "8px",
                  }}
                >
                  {/* Filter & Sort Button - Show only for sortable columns when allowFilters is true */}
                  {allowFilters && col.sortable && (
                    <button
                      type="button"
                      className={`filter-icon-btn btn btn-sm ${status.buttonClass}`}
                      onClick={(e) => toggleFilterPopover(col.columnKey, e)}
                      ref={(el) => {
                        filterButtonRefs.current[col.columnKey] = el;
                      }}
                      title={status.buttonTitle}
                      style={{
                        padding: "2px 6px",
                        minWidth: "30px",
                        height: "24px",
                        flexShrink: 0,
                        backgroundColor: "white",
                        border: "1px solid #ddd",
                      }}
                    >
                      <i
                        className={`bi ${status.buttonIcon}`}
                        style={{ fontSize: "12px" }}
                      ></i>
                    </button>
                  )}
                </div>
              </div>
            </div>
          );
        }
      }

      return columnDef;
    });
  }, [
    columns,
    filterableColumns,
    columnFilters,
    pendingFilters,
    dateRangeFilters,
    sortState,
    sortServer,
    gridState,
    allowFilters,
  ]);

  // Handle selection change
  const onSelectionChanged = useCallback(() => {
    if (!gridApi || !allowSelection) return;

    const selectedRows = gridApi.getSelectedRows();
    setSelectedRows(selectedRows);
  }, [gridApi, allowSelection]);

  // Clear selection when toggle changes
  useEffect(() => {
    if (gridApi && clearSelectionToggle) {
      gridApi.deselectAll();
    }
  }, [gridApi, clearSelectionToggle]);

  // Handle grid ready
  const onGridReady = useCallback(
    (params) => {
      setGridApi(params.api);
      setColumnApi(params.columnApi);

      // Apply saved column order if available
      if (columnOrder.length > 0 && params.columnApi) {
        try {
          const allColumns = params.columnApi.getAllColumns();
          if (allColumns) {
            const currentOrder = allColumns.map((col) => col.getColId());
            const pinnedLeftColumns = currentOrder.filter(
              (id) => id === "sr_no" || id.includes("Actions")
            );

            // Only use columns that exist in the grid
            const reorderableColumns = columnOrder.filter(
              (id) =>
                !pinnedLeftColumns.includes(id) && params.columnApi.getColumn(id)
            );

            const finalOrder = [...pinnedLeftColumns, ...reorderableColumns];
            params.columnApi.setColumnOrder(finalOrder);
          }
        } catch (error) {
          console.warn("Failed to apply initial column order:", error);
        }
      }
    },
    [columnOrder]
  );

  // Enhanced handleColumnMoved to prevent columns being dragged into pinned areas
  const enhancedHandleColumnMoved = useCallback(
    (params) => {
      // Check if this is a user-initiated drag
      if (params.source !== "uiColumnMoved") {
        return;
      }

      // Get the moved column ID
      const movedColumn = params.columns?.[0];
      if (!movedColumn) return;

      const movedColumnId = movedColumn.getColId();

      // Check if trying to move a regular column into pinned area
      const isPinnedColumn =
        movedColumnId === "sr_no" || movedColumnId.includes("Actions");
      if (isPinnedColumn) {
        return;
      }

      // Get current column state
      const columnState = params.api.getColumnState();

      // Find index of moved column
      const movedIndex = columnState.findIndex(
        (col) => col.colId === movedColumnId
      );
      if (movedIndex === -1) return;

      // Check if column is being moved into pinned area
      const leftPinnedCount = columnState.filter(
        (col) => col.pinned === "left"
      ).length;
      const rightPinnedCount = columnState.filter(
        (col) => col.pinned === "right"
      ).length;
      const totalColumns = columnState.length;

      // If moved column is now in left pinned area (before Sr.No)
      if (movedIndex < leftPinnedCount && !columnState[movedIndex].pinned) {
        // Reset column order to prevent this
        setTimeout(() => {
          if (columnApi) {
            try {
              const currentGridOrder = columnApi
                .getAllColumns()
                .map((col) => col.getColId());
              const pinnedLeftColumns = currentGridOrder.filter(
                (id) => id === "sr_no" || id.includes("Actions")
              );
              const reorderableColumns = columnOrder.filter(
                (id) =>
                  !pinnedLeftColumns.includes(id) && columnApi.getColumn(id)
              );
              const finalOrder = [...pinnedLeftColumns, ...reorderableColumns];
              columnApi.setColumnOrder(finalOrder);
            } catch (error) {
              console.warn("Failed to reset column order:", error);
            }
          }
        }, 50);
        return;
      }

      // If moved column is now in right pinned area (after Actions)
      if (
        movedIndex >= totalColumns - rightPinnedCount &&
        !columnState[movedIndex].pinned
      ) {
        // Reset column order to prevent this
        setTimeout(() => {
          if (columnApi) {
            try {
              const currentGridOrder = columnApi
                .getAllColumns()
                .map((col) => col.getColId());
              const pinnedLeftColumns = currentGridOrder.filter(
                (id) => id === "sr_no" || id.includes("Actions")
              );
              const reorderableColumns = columnOrder.filter(
                (id) =>
                  !pinnedLeftColumns.includes(id) && columnApi.getColumn(id)
              );
              const finalOrder = [...pinnedLeftColumns, ...reorderableColumns];
              columnApi.setColumnOrder(finalOrder);
            } catch (error) {
              console.warn("Failed to reset column order:", error);
            }
          }
        }, 50);
        return;
      }

      // If valid move, process as before
      handleColumnMoved(params);
    },
    [handleColumnMoved, columnApi, columnOrder]
  );

  // Render column management panel - SIMPLE FIX: SHOW IN SAME ORDER AS TABLE
  const renderColumnManagementPanel = () => {
    if (!showColumnPanel) return null;

    const availableScreenColumns = Array.isArray(screenColumns)
      ? screenColumns
      : [];

    // Filter out Sr.No and Actions columns
    const availableColumns = availableScreenColumns.filter(
      (col) => col.columnKey !== "Sr.No" && col.columnKey !== "Actions"
    );

    // Create a map to get screen column by columnKey
    const screenColumnByKey = {};
    availableColumns.forEach((col) => {
      screenColumnByKey[col.columnKey] = col;
    });

    // Get visible columns in the SAME ORDER as they appear in the table
    const visibleColumnsInTableOrder = columnOrder
      .filter(
        (colKey) =>
          columnVisibility[colKey] !== false && screenColumnByKey[colKey]
      )
      .map((colKey) => screenColumnByKey[colKey]);

    // Get hidden columns (all columns that are not visible)
    const hiddenColumns = availableColumns.filter(
      (col) => columnVisibility[col.columnKey] === false
    );

    return (
      <div
        className="column-management-panel card shadow-lg border-0 mt-4"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="card-header card-header-management text-white d-flex justify-content-between align-items-center">
          <h6 className="mb-0 text-light">Column Management</h6>
          <button
            type="button"
            className="btn-close btn-close-white"
            onClick={() => setShowColumnPanel(false)}
            aria-label="Close"
          />
        </div>
        <div className="card-body">
          {hasUnsavedOrderChanges && !hasJustSaved && (
            <div className="mb-3">
              <div className="alert alert-warning d-flex align-items-center justify-content-between p-2">
                <small className="d-flex align-items-center">
                  <i className="bi bi-exclamation-triangle me-2"></i>
                  <span>Column order changed</span>
                </small>
                <button
                  className="btn btn-warning btn-sm d-flex align-items-center"
                  onClick={saveColumnOrder}
                  disabled={isUpdatingColumns}
                >
                  <i className="bi bi-save me-1"></i>
                  {isUpdatingColumns ? "Saving..." : "Save"}
                </button>
              </div>
            </div>
          )}

          {/* Success message when just saved */}
          {hasJustSaved && (
            <div className="mb-3">
              <div className="alert alert-success d-flex align-items-center p-2">
                <i className="bi bi-check-circle me-2"></i>
                <small className="fw-bold">
                  Column order saved successfully!
                </small>
              </div>
            </div>
          )}
          <div className="mb-3">
            <div className="d-flex justify-content-between align-items-center mb-2">
              <small className="text-muted">
                Drag & drop columns to reorder
              </small>
              <button
                className="btn btn-outline-secondary btn-sm"
                onClick={resetColumnOrder}
                title="Reset to default column order"
              >
                <i className="bi bi-arrow-counterclockwise"></i>
              </button>
            </div>

            {/* Show count of visible columns */}
            <div className="badge bg-info text-dark mb-2">
              {visibleColumnsInTableOrder.length} of {availableColumns.length}{" "}
              columns visible
            </div>
          </div>

          {/* Visible Columns Section - SHOW IN SAME ORDER AS TABLE */}
          {visibleColumnsInTableOrder.length > 0 && (
            <div className="mb-3">
              <small className="text-muted mb-2 d-block">
                Visible Columns (Drag to reorder):
              </small>
              <div className="list-group mb-3">
                {visibleColumnsInTableOrder.map((col, index) => {
                  const colKey = col.columnKey;

                  return (
                    <div
                      key={colKey}
                      className="list-group-item list-group-item-action d-flex justify-content-between align-items-center draggable-column-item"
                      style={{ cursor: "move" }}
                      draggable="true"
                      onDragStart={(e) => {
                        e.dataTransfer.setData("text/plain", colKey);
                        e.dataTransfer.effectAllowed = "move";
                        e.currentTarget.classList.add("dragging");
                      }}
                      onDragEnd={(e) => {
                        e.currentTarget.classList.remove("dragging");
                      }}
                      onDragOver={(e) => {
                        e.preventDefault();
                        e.dataTransfer.dropEffect = "move";
                        e.currentTarget.classList.add("drag-over");
                      }}
                      onDragLeave={(e) => {
                        e.currentTarget.classList.remove("drag-over");
                      }}
                      onDrop={(e) => {
                        e.preventDefault();
                        e.currentTarget.classList.remove("drag-over");

                        const draggedColKey = e.dataTransfer.getData(
                          "text/plain"
                        );
                        if (draggedColKey && draggedColKey !== colKey) {
                          // Get current order from columnOrder state
                          const newOrder = [...columnOrder];
                          const draggedIndex = newOrder.indexOf(draggedColKey);
                          const targetIndex = newOrder.indexOf(colKey);

                          if (draggedIndex !== -1 && targetIndex !== -1) {
                            // Reorder array
                            newOrder.splice(draggedIndex, 1);
                            newOrder.splice(targetIndex, 0, draggedColKey);

                            // Update state
                            setColumnOrder(newOrder);
                            setUnsavedColumnOrder(newOrder);
                            setHasUnsavedOrderChanges(true);
                            setHasJustSaved(false);

                            // Apply to grid if available
                            if (columnApi && newOrder.length > 0) {
                              try {
                                const allColumns = columnApi.getAllColumns();
                                if (allColumns) {
                                  const currentGridOrder = allColumns.map(
                                    (col) => col.getColId()
                                  );
                                  const pinnedLeftColumns =
                                    currentGridOrder.filter(
                                      (id) =>
                                        id === "sr_no" || id.includes("Actions")
                                    );
                                  const reorderableColumns = newOrder.filter(
                                    (id) =>
                                      !pinnedLeftColumns.includes(id) &&
                                      columnApi.getColumn(id)
                                  );

                                  const finalOrder = [
                                    ...pinnedLeftColumns,
                                    ...reorderableColumns,
                                  ];
                                  columnApi.setColumnOrder(finalOrder);
                                }
                              } catch (error) {
                                console.warn(
                                  "Failed to apply new order to grid:",
                                  error
                                );
                              }
                            }
                          }
                        }
                      }}
                    >
                      <div className="d-flex align-items-center">
                        <i className="bi bi-grip-vertical text-muted me-2"></i>
                        <span>{col.columnName || col.columnKey}</span>
                        {col.sortable && (
                          <span
                            className="ms-2 badge bg-info"
                            title="Sortable column"
                          >
                            <i className="bi bi-sort-down"></i>
                          </span>
                        )}
                      </div>
                      <div className="form-check form-switch">
                        <input
                          className="form-check-input"
                          type="checkbox"
                          role="switch"
                          id={`toggle-${colKey}`}
                          checked={true}
                          onChange={(e) =>
                            toggleColumnVisibility(colKey, e.target.checked)
                          }
                        />
                        <label
                          className="form-check-label"
                          htmlFor={`toggle-${colKey}`}
                        >
                          Visible
                        </label>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Hidden Columns Section */}
          {hiddenColumns.length > 0 && (
            <div>
              <small className="text-muted mb-2 d-block">Hidden Columns:</small>
              <div className="list-group">
                {hiddenColumns.map((col) => {
                  const colKey = col.columnKey;
                  return (
                    <div
                      key={colKey}
                      className="list-group-item list-group-item-action d-flex justify-content-between align-items-center"
                    >
                      <div className="d-flex align-items-center">
                        <i className="bi bi-eye-slash text-muted me-2"></i>
                        <span className="text-muted">
                          {col.columnName || col.columnKey}
                        </span>
                        {col.sortable && (
                          <span
                            className="ms-2 badge bg-info"
                            title="Sortable column"
                          >
                            <i className="bi bi-sort-down"></i>
                          </span>
                        )}
                      </div>
                      <div className="form-check form-switch">
                        <input
                          className="form-check-input"
                          type="checkbox"
                          role="switch"
                          id={`toggle-hidden-${colKey}`}
                          checked={false}
                          onChange={(e) => {
                            toggleColumnVisibility(colKey, e.target.checked);
                            // When making a column visible, add it to the end of the order
                            if (e.target.checked) {
                              const newOrder = [...columnOrder, colKey];
                              setColumnOrder(newOrder);
                              setUnsavedColumnOrder(newOrder);
                              setHasUnsavedOrderChanges(true);
                              setHasJustSaved(false);
                            }
                          }}
                        />
                        <label
                          className="form-check-label text-muted"
                          htmlFor={`toggle-hidden-${colKey}`}
                        >
                          Hidden
                        </label>
                        </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </div>
    );
  };

  // Render floating popover - UPDATED WITH STATUS DROPDOWN (FIXED for 0 and 1)
  const renderFilterPopover = () => {
    if (!activeFilterPopover) return null;

    const filterableColumn = filterableColumns?.find(
      (fc) => fc.columnKey === activeFilterPopover
    );
    if (!filterableColumn) return null;

    const buttonEl = filterButtonRefs.current[activeFilterPopover];
    if (!buttonEl) return null;

    const rect = buttonEl.getBoundingClientRect();

    const currentFilter = getFilterDisplayValue(filterableColumn.columnKey);
    const currentDateRange = getDateRangeFilterValue(
      filterableColumn.columnKey
    );
    const hasPendingChanges =
      pendingFilters[filterableColumn.columnKey] !== undefined;
    const hasDateRangeChanges =
      currentDateRange.startDate || currentDateRange.endDate;
    const colType = filterableColumn.type || "string";

    // Check if this is a date column
    const isDateColumn = colType === "date";
    // Check if this is a status column
    const isStatusColumn = colType === "status";

    return (
      <div
        className="floating-filter-popover"
        style={{
          position: "fixed",
          top: `${rect.bottom + 5}px`,
          left: `${rect.left - (isDateColumn ? 250 : 100)}px`,
          zIndex: 9999,
          width: isDateColumn ? "500px" : "300px",
        }}
        key={activeFilterPopover}
      >
        <div className="filter-popover" onClick={(e) => e.stopPropagation()}>
          <div className="popover-header">
            <div className="d-flex justify-content-between align-items-center">
              <h6 className="mb-0 text-light">
                <i className="bi bi-sliders me-2"></i>
                {filterableColumn.label} - Filter & Sort
              </h6>
              <button
                type="button"
                className="btn-close btn-sm"
                onClick={cancelFilterChanges}
                aria-label="Close"
              />
            </div>
          </div>

          <div className="popover-body">
            {/* Sorting Dropdown - Show only if sortServer is enabled */}
            {sortServer && (
              <div className="mb-3">
                <label className="form-label">
                  <i className="bi bi-sort-down me-1"></i>
                  Sort Order
                </label>
                <select
                  className="form-select"
                  value={sortState.column === activeFilterPopover ? sortState.direction || "" : ""}
                  onChange={(e) => {
                    const direction = e.target.value;
                    handleSortChange(activeFilterPopover, direction || null);
                  }}
                >
                  <option value="">Default (No Sorting)</option>
                  <option value="asc">Ascending (A-Z)</option>
                  <option value="desc">Descending (Z-A)</option>
                </select>
                <div className="mt-1">
                  <small className="text-muted">
                    <i className="bi bi-info-circle me-1"></i>
                    Sorting applies immediately
                  </small>
                </div>
                {sortState.column === activeFilterPopover && sortState.direction && (
                  <div className="mt-1">
                    <div className="alert alert-info py-1 px-2 mb-0">
                      <small>
                        <i className="bi bi-check-circle me-1"></i>
                        Currently sorted {sortState.direction === "asc" ? "ascending (A-Z)" : "descending (Z-A)"}
                      </small>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Separator */}
            <hr className="my-3" />

            {/* Filter Section */}
            <div className="mb-3">
              <label className="form-label">
                <i className="bi bi-funnel me-1"></i>
                Filter
              </label>
              
              {isDateColumn ? (
                // Date Range Filter
                <div className="date-range-filter row">
                  <div className="col-12 col-md-12 mb-3">
                    <label className="form-label">Start Date</label>
                    <input
                      ref={popoverInputRef}
                      type="datetime-local"
                      className="form-control"
                      value={currentDateRange.startDate || ""}
                      onChange={(e) => {
                        const newStartDate = e.target.value;
                        const newEndDate = currentDateRange.endDate;
                        handleDateRangeChange(
                          filterableColumn.columnKey,
                          newStartDate,
                          newEndDate
                        );
                      }}
                    />
                  </div>
                  <div className="col-12 col-md-12 mb-3">
                    <label className="form-label">End Date</label>
                    <input
                      type="datetime-local"
                      className="form-control"
                      value={currentDateRange.endDate || ""}
                      onChange={(e) => {
                        const newEndDate = e.target.value;
                        const newStartDate = currentDateRange.startDate;
                        handleDateRangeChange(
                          filterableColumn.columnKey,
                          newStartDate,
                          newEndDate
                        );
                      }}
                    />
                  </div>
                  <div className="d-flex gap-2 mb-2">
                    <button
                      className="btn btn-sm btn-outline-secondary"
                      onClick={() => {
                        // Set today's date
                        const today = new Date();
                        const todayStr = today.toISOString().slice(0, 16);
                        handleDateRangeChange(
                          filterableColumn.columnKey,
                          todayStr,
                          todayStr
                        );
                      }}
                    >
                      Today
                    </button>
                    <button
                      className="btn btn-sm btn-outline-secondary"
                      onClick={() => {
                        // Set this week
                        const now = new Date();
                        const startOfWeek = new Date(
                          now.setDate(now.getDate() - now.getDay())
                        );
                        const endOfWeek = new Date(startOfWeek);
                        endOfWeek.setDate(endOfWeek.getDate() + 6);

                        const startStr = startOfWeek.toISOString().slice(0, 16);
                        const endStr = endOfWeek.toISOString().slice(0, 16);
                        handleDateRangeChange(
                          filterableColumn.columnKey,
                          startStr,
                          endStr
                        );
                      }}
                    >
                      This Week
                    </button>
                    <button
                      className="btn btn-sm btn-outline-secondary"
                      onClick={() => {
                        // Set this month
                        const now = new Date();
                        const startOfMonth = new Date(
                          now.getFullYear(),
                          now.getMonth(),
                          1
                        );
                        const endOfMonth = new Date(
                          now.getFullYear(),
                          now.getMonth() + 1,
                          0
                        );

                        const startStr = startOfMonth.toISOString().slice(0, 16);
                        const endStr = endOfMonth.toISOString().slice(0, 16);
                        handleDateRangeChange(
                          filterableColumn.columnKey,
                          startStr,
                          endStr
                        );
                      }}
                    >
                      This Month
                    </button>
                  </div>
                </div>
              ) : isStatusColumn ? (
                // Status column dropdown - sends number to server (FIXED for 0 and 1)
                <select
                  className="form-select mb-2"
                  value={currentFilter?.value !== undefined ? currentFilter.value : ""}
                  onChange={(e) => {
                    const selectedValue = e.target.value;
                    // For empty string, send empty value
                    if (selectedValue === "") {
                      handleFilterInputChange(
                        filterableColumn.columnKey,
                        "",
                        "EqualTo"
                      );
                    } else {
                      // Convert to number for status values
                      const numericValue = Number(selectedValue);
                      handleFilterInputChange(
                        filterableColumn.columnKey,
                        numericValue,
                        "EqualTo"
                      );
                    }
                  }}
                  ref={popoverInputRef}
                >
                  {STATUS_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              ) : colType === "boolean" ? (
                <select
                  className="form-select mb-2"
                  value={currentFilter?.value || ""}
                  onChange={(e) =>
                    handleFilterInputChange(
                      filterableColumn.columnKey,
                      e.target.value,
                      "EqualTo"
                    )
                  }
                >
                  <option value="">All Status</option>
                  <option value="true">Active</option>
                  <option value="false">Inactive</option>
                </select>
              ) : (
                // Regular text/number filter
                <>
                  {colType !== "number" && (
                    <select
                      className="form-select mb-3"
                      value={currentFilter?.operator || "Contains"}
                      onChange={(e) =>
                        handleFilterInputChange(
                          filterableColumn.columnKey,
                          currentFilter?.value || "",
                          e.target.value
                        )
                      }
                    >
                      {opsForType(colType).map((op) => (
                        <option key={op.value} value={op.value}>
                          {op.label}
                        </option>
                      ))}
                    </select>
                  )}
                  <input
                    ref={popoverInputRef}
                    type={colType === "number" ? "number" : "text"}
                    className="form-control"
                    placeholder={`Enter ${
                      colType === "number" ? "number" : "value"
                    }...`}
                    value={currentFilter?.value || ""}
                    onChange={(e) =>
                      handleFilterInputChange(
                        filterableColumn.columnKey,
                        e.target.value,
                        colType === "number"
                          ? "EqualTo"
                          : currentFilter?.operator || "Contains"
                      )
                    }
                  />
                </>
              )}

              <div className="mt-2">
                <small className="text-muted">
                  <i className="bi bi-info-circle me-1"></i>
                  {isDateColumn
                    ? "Date range will be converted to epoch seconds"
                    : isStatusColumn
                    ? "Status will be sent as number to server"
                    : "Click 'Apply Filter' to send filter to server"}
                </small>
              </div>
            </div>

            <div className="d-flex justify-content-between mt-3">
              <div className="d-flex gap-2">
                {/* Clear button for filter */}
                {(currentFilter?.value !== undefined && currentFilter?.value !== "") && (
                  <button
                    className="btn btn-sm btn-outline-danger"
                    onClick={() => clearColumnFilter(filterableColumn.columnKey)}
                  >
                    <i className="bi bi-x-circle me-1"></i>
                    Clear Filter
                  </button>
                )}
              </div>
              <button
                className="btn btn-sm btn-success"
                onClick={applyFilters}
                disabled={!hasPendingChanges && !hasDateRangeChanges}
              >
                <i className="bi bi-check-circle me-1"></i>
                Apply Filter
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Toolbar - UPDATED WITH SAVE BUTTON
  const Toolbar = () => {
    const hasPendingFilters = Object.keys(pendingFilters).length > 0;
    const hasActiveFilters = Object.keys(columnFilters).length > 0;
    const hasDateRangeFilters = Object.keys(dateRangeFilters).length > 0;
    const hasActiveSort = !!sortState.column;
    const hiddenColumnsCount = Object.values(columnVisibility).filter(
      (v) => v === false
    ).length;

    return (
      <>
        {allowToolbar && (
          <div className="card shadow-sm mb-2 border-0">
            <div className="card-body py-2 px-3 d-flex flex-wrap align-items-center gap-2">
              <div
                className="d-flex align-items-center me-auto"
                style={{ gap: 8 }}
              >
                {allowSelection && (
                  <span
                    className="text-dark d-flex align-items-center"
                    style={{ gap: 6 }}
                  >
                    <i className="bi bi-check2-square"></i>
                    <strong>Selection:</strong> {selectedRows.length}
                  </span>
                )}

                {/* NEW: Save Column Order Button - Shows when there are unsaved changes */}
                {hasUnsavedOrderChanges && !hasJustSaved && (
                  <button
                    className="btn btn-warning d-flex align-items-center"
                    onClick={saveColumnOrder}
                    disabled={isUpdatingColumns}
                    title="Save column order changes"
                  >
                    <i className="bi bi-save me-1"></i>
                    {isUpdatingColumns ? "Saving..." : "Save Order"}
                  </button>
                )}

                {/* Success message when just saved */}
                {hasJustSaved && (
                  <div className="alert alert-success d-flex align-items-center py-1 px-3 mb-0">
                    <i className="bi bi-check-circle me-2"></i>
                    <small className="fw-bold">Order saved!</small>
                  </div>
                )}
              </div>

              {/* Filter Operation Dropdown - Only shows when 2 or more filters are active */}
              {(hasActiveFilters || hasDateRangeFilters) &&
                Object.keys(columnFilters).length +
                  Object.keys(dateRangeFilters).length >
                  1 &&
                allowFilters && (
                  <div className="btn-group">
                    <button
                      type="button"
                      className="btn btn-outline-info dropdown-toggle d-flex align-items-center"
                      data-bs-toggle="dropdown"
                      aria-expanded="false"
                      title={`Current: ${filterOperation} - Click to change`}
                    >
                      <i className="bi bi-filter-circle me-2"></i>
                      {filterOperation}
                    </button>
                    <ul className="dropdown-menu dropdown-menu-end">
                      <li>
                        <button
                          className={`dropdown-item d-flex align-items-center ${
                            filterOperation === "AND" ? "active" : ""
                          }`}
                          onClick={() => handleFilterOperationChange("AND")}
                        >
                          <i
                            className="bi bi-check-lg me-2"
                            style={{
                              visibility:
                                filterOperation === "AND"
                                  ? "visible"
                                  : "hidden",
                            }}
                          ></i>
                          AND (All filters must match)
                        </button>
                      </li>
                      <li>
                        <button
                          className={`dropdown-item d-flex align-items-center ${
                            filterOperation === "OR" ? "active" : ""
                          }`}
                          onClick={() => handleFilterOperationChange("OR")}
                        >
                          <i
                            className="bi bi-check-lg me-2"
                            style={{
                              visibility:
                                filterOperation === "OR"
                                  ? "visible"
                                  : "hidden",
                            }}
                          ></i>
                          OR (Any filter can match)
                        </button>
                      </li>
                    </ul>
                  </div>
                )}

              {/* Column Management Button - Now includes indicator for unsaved changes */}
              <button
                className="btn btn-outline-primary d-flex align-items-center column-management-btn"
                onClick={() => setShowColumnPanel(!showColumnPanel)}
                title="Manage columns"
              >
                <i className="bi bi-columns me-2"></i>Columns
                {hasUnsavedOrderChanges && !hasJustSaved && (
                  <span className="ms-1 badge bg-warning">
                    <i className="bi bi-exclamation"></i>
                  </span>
                )}
              </button>

              {allowCreation && !isManager && (
                <button
                  className="btn btn-success d-flex align-items-center"
                  onClick={onCreate}
                >
                  <i className="bi bi-plus-lg me-2"></i>Create
                </button>
              )}

              {allowFilters &&
                (hasActiveFilters || hasPendingFilters || hasDateRangeFilters) && (
                  <div className="d-flex gap-2">
                    {(hasPendingFilters > 0 || hasDateRangeFilters > 0) && (
                      <button
                        className="btn btn-warning d-flex align-items-center"
                        onClick={applyFilters}
                      >
                        <i className="bi bi-check-circle me-2"></i>Apply Filters
                      </button>
                    )}

                    {(hasActiveFilters > 0 || hasDateRangeFilters > 0) && (
                      <button
                        className="btn btn-outline-danger d-flex align-items-center"
                        onClick={clearAllFilters}
                      >
                        <i className="bi bi-x-circle me-2"></i>Clear Filters
                      </button>
                    )}
                  </div>
                )}

              {isDownloadTemplate && (
                <div className="btn-group">
                  <button
                    type="button"
                    className="btn btn-outline-primary dropdown-toggle d-flex align-items-center"
                    data-bs-toggle="dropdown"
                    aria-expanded="false"
                  >
                    <i className="bi bi-list-check me-2"></i>Bulk Actions
                  </button>
                  <ul className="dropdown-menu dropdown-menu-end">
                    <li>
                      <a
                        onClick={() => setShowUploadModal(true)}
                        className="d-flex align-items-center justify-content-start ps-3 pt-3 cursor-pointer"
                      >
                        Upload Excel
                      </a>
                    </li>
                    <li>
                      <a
                        href={
                          process.env.REACT_APP_STORAGE_URL + "template.xlsx"
                        }
                        className="d-flex align-items-center justify-content-start ps-3 py-3"
                      >
                        Download Template
                      </a>
                    </li>
                  </ul>
                </div>
              )}

              {allowBulkActions && selectedRows.length >= 2 && (
                <div className="btn-group">
                  <button
                    type="button"
                    className="btn btn-outline-primary dropdown-toggle d-flex align-items-center"
                    data-bs-toggle="dropdown"
                    aria-expanded="false"
                  >
                    <i className="bi bi-list-check me-2"></i>Bulk Actions
                  </button>
                  <ul className="dropdown-menu dropdown-menu-end">
                    <li>
                      <button
                        className="dropdown-item d-flex align-items-center"
                        onClick={bulkExport}
                      >
                        <i className="bi bi-download me-2"></i>Export Selected
                      </button>
                    </li>
                  </ul>
                </div>
              )}
            </div>
          </div>
        )}
      </>
    );
  };

  useEffect(() => {
    dispatch(
      success(
        getUserData().userTypeId == typeConstants.MANAGER,
        recruiterConstants.IS_MANAGER
      )
    );
  }, [dispatch]);

  // Initialize Bootstrap dropdowns
  useEffect(() => {
    const initializeDropdowns = () => {
      const dropdownElements = document.querySelectorAll(".dropdown-toggle");
      dropdownElements.forEach((dropdown) => {
        if (!dropdown.classList.contains("bs-dropdown-initialized")) {
          new window.bootstrap.Dropdown(dropdown);
          dropdown.classList.add("bs-dropdown-initialized");
        }
      });
    };

    const timeoutId = setTimeout(initializeDropdowns, 100);

    if (gridApi && data) {
      gridApi.addEventListener("modelUpdated", initializeDropdowns);
      gridApi.addEventListener("sortChanged", initializeDropdowns);
      gridApi.addEventListener("filterChanged", initializeDropdowns);
    }

    return () => {
      clearTimeout(timeoutId);
      if (gridApi) {
        gridApi.removeEventListener("modelUpdated", initializeDropdowns);
        gridApi.removeEventListener("sortChanged", initializeDropdowns);
        gridApi.removeEventListener("filterChanged", initializeDropdowns);
      }
    };
  }, [gridApi, data]);

  // Fetch screen columns and user preferences
  useEffect(() => {
    if (screenId) {
      dispatch(screenActions.getColumnsByScreenId(screenId));
      dispatch(screenActions.getUserPreferencesByScreenId(screenId));
    }
  }, [screenId, dispatch]);

  // AG Grid configuration - FIXED to hide AG Grid's default loader
  const gridOptions = useMemo(
    () => ({
      columnDefs: getAGGridColumns,
      rowData: data,
      animateRows: true,
      rowSelection: allowSelection ? "multiple" : undefined,
      suppressServerSideFilter: true,

      enableSorting: false,
      suppressServerSideSorting: true,

      onGridReady: onGridReady,
      // onFirstDataRendered: (params) => {
      //   // First size columns to fit the container
      //   params.api.sizeColumnsToFit();
        
      //   // Then auto-size all columns to fit content
      //   // const allColumnIds =
      //   //   params.api.getColumns()?.map((column) => column.getColId()) || [];
      //   // params.api.autoSizeColumns(allColumnIds, false);

      //   // // Ensure minimum widths are respected
      //   // allColumnIds.forEach((colId) => {
      //   //   const column = params.api.getColumn(colId);
      //   //   if (column) {
      //   //     const currentWidth = column.getActualWidth();
      //   //     const minWidth = column.getMinWidth() || 100;
      //   //     if (currentWidth < minWidth) {
      //   //       params.api.setColumnWidth(colId, minWidth);
      //   //     }
      //   //   }
      //   // });

      //   // // Finally, size columns to fit again to ensure they fit the container
      //   // params.api.sizeColumnsToFit();
      // },

      // // New: Handle when data changes after initial load
      // onRowDataUpdated: (params) => {
      //   // Auto-size columns when new data is loaded
      //   // const allColumnIds =
      //   //   params.api.getColumns()?.map((column) => column.getColId()) || [];
        
      //   // // Auto-size to fit content
      //   // params.api.autoSizeColumns(allColumnIds, false);
        
      //   // // Ensure minimum widths
      //   // allColumnIds.forEach((colId) => {
      //   //   const column = params.api.getColumn(colId);
      //   //   if (column) {
      //   //     const currentWidth = column.getActualWidth();
      //   //     const minWidth = column.getMinWidth() || 100;
      //   //     if (currentWidth < minWidth) {
      //   //       params.api.setColumnWidth(colId, minWidth);
      //   //     }
      //   //   }
      //   // });
        
      //   // Size to fit container
      //   // params.api.sizeColumnsToFit();
      // },

      getRowHeight: (params) => {
        if (params.node.group) {
          return 40;
        }
        return "auto";
      },

      defaultColDef: {
        sortable: false,
        filter: false,
        resizable: true,
        wrapText: true,
        autoHeight: true,
        cellStyle: (params) => {
          // Add zebra striping based on row index
          const backgroundColor =
            params.node.rowIndex % 2 === 0 ? "#ffffff" : "#f8f9fa"; // White for even rows, Light grey for odd rows

          return {
            whiteSpace: "normal",
            overflow: "visible",
            textOverflow: "clip",
            wordBreak: "break-word",
            lineHeight: "1.5",
            display: "flex",
            alignItems: "center",
            textAlign: "left", // Default left alignment for all cells
            backgroundColor: backgroundColor,
          };
        },
        // Enable column dragging at the default level
        suppressMovable: false,
        headerClass: "black-header", // Added class for all headers
      },
      onSelectionChanged: onSelectionChanged,

      // Add column moved event handler - Only tracks changes locally
      onColumnMoved: enhancedHandleColumnMoved,
      suppressDragLeaveHidesColumns: true,

      // Enable column dragging globally
      suppressColumnMove: false,
      suppressColumnVirtualisation: true,

      domLayout: "normal",
      
      // IMPORTANT: Disable AG Grid's default loading overlay
      overlayNoRowsTemplate:
        '<span className="p-3">No data to display</span>',
      overlayLoadingTemplate: 
        '<span className="p-3 d-none"></span>', // Hide AG Grid's default loading overlay

      onGridSizeChanged: (params) => {
        // When grid size changes, resize columns to fit
        params.api.sizeColumnsToFit();
      },

      // New: Add event listener for when data is set
      // onModelUpdated: (params) => {
      //   // When model is updated (data loaded), resize columns
      //   setTimeout(() => {
      //     params.api.sizeColumnsToFit();
          
      //     // Also auto-size to fit content
      //     const allColumnIds = params.api.getColumns()?.map((column) => column.getColId()) || [];
      //     params.api.autoSizeColumns(allColumnIds, false);
      //   }, 0);
      // },
    }),
    [
      getAGGridColumns,
      data,
      allowSelection,
      onSelectionChanged,
      onGridReady,
      enhancedHandleColumnMoved,
    ]
  );

  // Effect to resize columns when data changes
  // useEffect(() => {
  //   if (gridApi && data && data.length > 0) {
  //     // Use setTimeout to ensure DOM is updated
  //     setTimeout(() => {
  //       try {
  //         // First size to fit container
  //         gridApi.sizeColumnsToFit();
          
  //         // Then auto-size to content
  //         const allColumnIds = gridApi.getColumns()?.map((column) => column.getColId()) || [];
  //         gridApi.autoSizeColumns(allColumnIds, false);
          
  //         // Ensure minimum widths
  //         allColumnIds.forEach((colId) => {
  //           const column = gridApi.getColumn(colId);
  //           if (column) {
  //             const currentWidth = column.getActualWidth();
  //             const minWidth = column.getMinWidth() || 100;
  //             if (currentWidth < minWidth) {
  //               gridApi.setColumnWidth(colId, minWidth);
  //             }
  //           }
  //         });
          
  //         // Final size to fit
  //         gridApi.sizeColumnsToFit();
  //       } catch (error) {
  //         console.warn("Error resizing columns:", error);
  //       }
  //     }, 0);
  //   }
  // }, [gridApi, data]);

  // Effect to resize columns when column order or visibility changes
  useEffect(() => {
    if (gridApi && columnOrder.length > 0) {
      setTimeout(() => {
        try {
          gridApi.sizeColumnsToFit();
          const allColumnIds = gridApi.getColumns()?.map((column) => column.getColId()) || [];
          gridApi.autoSizeColumns(allColumnIds, false);
        } catch (error) {
          console.warn("Error resizing columns after column changes:", error);
        }
      }, 100);
    }
  }, [gridApi, columnOrder, columnVisibility]);

  // SIMPLE RENDER LOGIC: Show loader or table
  // Show only custom loader while loading
  if (isLoading) {
    return (
      <div className="actions-datatable-container">
        <UploadExcelModal
          show={showModal}
          onHide={() => setShowUploadModal(false)}
        />
        <TableLoader />
      </div>
    );
  }

  // Show table only when data is fully loaded
  return (
    <div className="actions-datatable-container">
      <UploadExcelModal
        show={showModal}
        onHide={() => setShowUploadModal(false)}
      />
      <Toolbar />
      {renderFilterPopover()}
      {renderColumnManagementPanel()}
      
      <div
        className="ag-theme-alpine"
      >
        <AgGridReact {...gridOptions} ref={gridRef} pagination={false} />
      </div>
      <ServerPagination
        currentPage={gridState.pageNumber}
        rowsPerPage={gridState.length}
        totalRows={paginationTotalRows}
        onPageChange={onChangePage}
        onRowsPerPageChange={onChangeRowsPerPage}
      />
    </div>
  );
}